//
// PimTreeStore
//
// The PimTreeStore handles interaction with the Pim Navigation Tree (TreeStore)
//
// The exposed store is the actual TreeStore (.tree), but that is a generic element
// and the PimTreeStore handles storing view choices like expandedIds and such, and
// fetching data based on that and the current, possibly changeable, environment.
//
// In a store, we use it like this:
//
//     this._pimtree = new PimTreeStore("/records/tree", this._rootstore, this._environment)
//      ...
//     get pimtree() {
//         if (!this.user && !this.project) return null
//         this._pimtree.loadOnce()
//         return this._pimtree.tree // NOTE the .tree!
//     }
//      ...
//     _pimtree is observable, pimtree is computed
//
// Have a look at the TreeNavigator component to see how it is used in rendering.
//
// SIDE-EFFECT: when a user selects a treeitem, we update the pimworksheet with that
//              same record

import { action, makeObservable, observable, reaction, runInAction } from 'mobx'

import { LatestRequestGate } from '../../utils/concurrency'
import { TreeStore, TreeStoreItem } from '../components/TreeStore'

const BUFFER_FACTOR = 2

export class PimTreeStore {
    __init = false
    _key = null
    _rootstore = null
    _environment = null
    tree = null
    firstVisibleIndex = null
    firstRecordIndex = null
    fetchedFirstVisibleIndex = null
    windowSize = null

    constructor(key, rootstore, environment) {
        makeObservable(this, {
            __init: observable,
            _key: observable,
            _rootstore: observable,
            _environment: observable,
            tree: observable,
            firstVisibleIndex: observable,
            firstRecordIndex: observable,
            windowSize: observable,
            fetch: action.bound,
            refetch: action.bound,
            _fetch: action.bound,
            select: action.bound,
            selectRecord: action.bound,
            setRecord: action.bound,
            create: action.bound,
            syncdataitem_callback: action.bound,
        })

        this._key = key
        this._rootstore = rootstore
        this._environment = environment
        this.tree = new TreeStore(this)
        this.firstVisibleIndex = 0
        this.firstRecordIndex = 0
        this.windowSize = 10
        this._syncdebounce = null
        this._syncdelay = 100
        this._treeRequestGate = new LatestRequestGate(2)
    }

    loadOnce = () => {
        if (this.__init) return
        const parentGids = this._rootstore.view.loadPerProject(
            this._key + '/expandedGids',
            []
        )
        const selectedGid = this._rootstore.view.loadPerProject(
            this._key + '/selectedGid'
        )
        this.firstVisibleIndex = this._rootstore.view.loadPerProject(
            this._key + '/topindex',
            0
        )
        this.windowSize = this._rootstore.view.loadPerProject(
            this._key + '/windowsize',
            10
        )
        this.tree._expandedIds.replace(parentGids)
        this._fetch(parentGids, this.firstVisibleIndex, this.windowSize).then(() => {
            runInAction(() => {
                this.tree._selectedId = selectedGid
                this.tree.multiselection.rangeStart(selectedGid)
            })
            reaction(
                () => ({
                    selectedGid: this.tree._selectedId,
                    selectionsize: this.tree.multiselection.size,
                }),
                ({ selectedGid, selectionsize }) => {
                    if (selectedGid && !selectionsize) {
                        this.tree.multiselection.rangeStart(selectedGid)
                    }
                }
            )
            reaction(
                () => this._environment.get('language'),
                language => {
                    this._fetch(
                        this.tree._expandedIds,
                        this.firstVisibleIndex,
                        this.windowSize,
                        undefined,
                        true
                    )
                }
            )
            reaction(
                () => this._environment.get('ordering'),
                ordering => {
                    this._fetch(
                        this.tree._expandedIds,
                        this.firstVisibleIndex,
                        this.windowSize
                    )
                }
            )
        })
        this._rootstore.api.register_syncdataitem_callback(this.syncdataitem_callback)
        this.__init = true
    }

    fetch(parentGids, firstVisibleIndex, windowSize) {
        if (firstVisibleIndex !== undefined) this.firstVisibleIndex = firstVisibleIndex
        if (windowSize !== undefined) this.windowSize = windowSize
        if (parentGids !== undefined) {
            this._rootstore.view.savePerProject(this._key + '/expandedGids', parentGids)
        }
        this._rootstore.view.savePerProject(
            this._key + '/topindex',
            this.firstVisibleIndex
        )
        this._rootstore.view.savePerProject(this._key + '/windowsize', windowSize)
        return this._fetch(parentGids, this.firstVisibleIndex, this.windowSize)
    }

    refetch(firstVisibleIndex, windowSize, throttleNearbyIndexes) {
        const windowSizeChanged = windowSize !== this.windowSize

        if (firstVisibleIndex !== undefined) {
            this.firstVisibleIndex = firstVisibleIndex
            this._rootstore.view.savePerProject(
                this._key + '/topindex',
                firstVisibleIndex
            )
        }
        if (windowSize !== undefined) {
            this.windowSize = windowSize
            this._rootstore.view.savePerProject(this._key + '/windowsize', windowSize)
        }

        if (throttleNearbyIndexes) {
            const difference = Math.abs(
                firstVisibleIndex - this.fetchedFirstVisibleIndex
            )
            const margin = windowSize * BUFFER_FACTOR * 0.5
            if (!windowSizeChanged && difference < margin) {
                return
            }
        }

        return this._fetch(
            this.tree._expandedIds,
            this.firstVisibleIndex,
            this.windowSize
        )
    }

    scrollIntoView(recordGid) {
        return this._fetch(
            this.tree._expandedIds,
            this.firstVisibleIndex,
            this.windowSize,
            recordGid
        )
    }

    async _fetch(
        parentGids,
        firstVisibleIndex,
        windowSize,
        recordGid,
        refetchData = false
    ) {
        if (firstVisibleIndex !== undefined) {
            this.fetchedFirstVisibleIndex = firstVisibleIndex
        }
        let params = {
            expanded_records: parentGids,
            'page.top': firstVisibleIndex,
            'page.size': windowSize,
            'page.buffer': windowSize * BUFFER_FACTOR,
            language: this._environment.get('language'),
            ordering: this._environment.get('ordering'),
            omit_data_for: refetchData ? [] : this.tree.keys,
        }
        if (recordGid) {
            params['selected_record'] = recordGid
        }
        return this._treeRequestGate
            .run(signal => this._rootstore._fetch('/records/tree', params, { signal }))
            .then(result => {
                if (!result) {
                    return result
                }
                runInAction(() => {
                    // and use it to populate treestore items
                    // result tree is a list of gids, in order
                    const treeitems = result['tree']
                        .map(recordgid => {
                            const record = this._rootstore.data.records.get(recordgid)
                            if (!record) return null
                            return new TreeStoreItem(
                                record.gid,
                                record.parent,
                                record.treelevel,
                                record.childcount,
                                record
                            )
                        })
                        .filter(treeitem => treeitem !== null)
                    // update tree
                    if ('expanded_records' in result) {
                        parentGids = result['expanded_records']
                        this._rootstore.view.savePerProject(
                            this._key + '/expandedGids',
                            parentGids
                        )
                    }
                    this.tree._expandedIds.replace(parentGids)

                    if ('page.top' in result) {
                        this.firstVisibleIndex = result['page.top']
                        this._rootstore.view.savePerProject(
                            this._key + '/topindex',
                            result['page.top']
                        )
                    }

                    const firstRecordIndex =
                        result['page.records_top'] ??
                        result['page.top'] ??
                        firstVisibleIndex
                    if (typeof firstRecordIndex === 'undefined') {
                        throw new Error(
                            'PimTreeStore _fetch: cannot determine first record index'
                        )
                    }
                    this.firstRecordIndex = firstRecordIndex

                    this.fetchedFirstVisibleIndex =
                        result['page.top'] ?? firstVisibleIndex

                    this.tree.setTreeItems(treeitems, result['tree.size'])
                })
                return result
            })
        // .catch(error => {})
    }

    select(treeitem) {
        this.selectRecord(treeitem.item)
    }

    deselect(treeitem) {
        this.selectRecord(null)
    }

    selectRecord(record) {
        this._rootstore.view.savePerProject(
            this._key + '/selectedGid',
            record ? record.gid : null
        )
        this._rootstore.view.pimworksheet.setRecord(record)
    }

    async collapseToSelected() {
        const selectedId = this.tree._selectedId
        if (!selectedId) {
            return this.collapseToRoot()
        }

        const selectedItem = this._rootstore.data.records.get(selectedId)
        if (!selectedItem) {
            return this.collapseToRoot()
        }

        return this.tree._setExpandedIds(selectedItem.path)
    }

    async collapseToRoot() {
        const rootRecord = this.tree._treeitems.find(
            treeitem => treeitem.parentId === null
        )
        // in the PimTree, the treeitem ids are record gids
        return this.tree._setExpandedIds(rootRecord ? [rootRecord.id] : [])
    }

    setRecord(record) {
        // this is external; when we select an item, we need to make sure it appears in
        // the expanded tree, and that it will be the selected treeitem
        const parentpath = record.path.slice(1)
        const newExpandedGids = Array.from(
            new Set([...this.tree._expandedIds, ...parentpath])
        )
        this.tree._setExpandedIds(newExpandedGids)
        this.tree.setSelectedId(record.gid)
        this.tree.multiselection.deselectAll()
        this.tree.multiselection.rangeStart(record.gid)
    }

    create = parenttreeitem => {
        this._rootstore
            ._fetch('/records/create', {
                parent: parenttreeitem.item.gid,
                language: this._environment.get('language'),
                ordering: this._environment.get('ordering'),
            })
            .then(result => {
                // store data in store.data.pimrecords
                // add the newly created record as a treeitem
                // and update the parent treeitem
                const treeitems = ['record', 'parent']
                    .map(resultname => {
                        const recordgid = result[resultname]
                        const record = this._rootstore.data.records.get(recordgid)
                        if (!record) return null
                        const treeitem = new TreeStoreItem(
                            record.gid,
                            record.parent,
                            record.treelevel,
                            record.childcount,
                            record
                        )
                        return treeitem
                    })
                    .filter(treeitem => treeitem !== null)
                // update tree
                this.tree.expandItem(treeitems[1])
                this.tree.selectItem(treeitems[0])
                return result
            })
        // .catch(error => {})
    }

    schedule_refetch = () => {
        if (this._syncdebounce) {
            clearTimeout(this._syncdebounce)
            this._syncdebounce = null
        }
        this._syncdebounce = setTimeout(() => {
            this.refetch()
            this._syncdebounce = null
        }, this._syncdelay)
    }

    syncdataitem_callback = (syncdataitem, data_before, data_after) => {
        // console.log('PimTreeStore sync', syncdataitem, data_before, data_after)
        // we're only interested in data_type "records"
        // then, we're only interested in updates (of childcount)
        // from those records, only for parents that are in our store
        // from those parents, only when childcount has changed
        if (syncdataitem['data_type'] !== 'records') return
        if (syncdataitem['action'] === 'DELETE') {
            if (data_before && this.tree.findItemById(data_before.parent)) {
                this.schedule_refetch()
            }
            return
        }
        if (syncdataitem['action'] !== 'UPDATE') return
        const gid = data_before ? data_before.gid : data_after.gid
        if (!(this.tree.findItemById(gid) || this.tree.hasItemWithParentId(gid))) {
            return
        }
        if (data_before.childcount !== data_after.childcount) {
            this.schedule_refetch()
        }
    }
}
